import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { CourseContext } from '../contexts/CourseContext';
import '../styles/Dashboard.css';

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const { storeCourses } = useContext(CourseContext);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const handleCourseDetails = (courseId, courseName) => {
    // Navigate to course details page where user selects group
    navigate(`/course/${courseId}`, { state: { courseName } });
  };

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get('/api/courses');
      
      // API returns { courses: [...], count: N, timestamp: "..." }
      const courseData = response.data.courses || [];
      const coursesArray = Array.isArray(courseData) ? courseData : [];
      
      // Sort courses by numeric prefix (e.g., "1 - ", "2 - ", "10 - ")
      const sorted = coursesArray.sort((a, b) => {
        const numA = parseInt(a.fullname.match(/^\d+/)?.[0] || 999);
        const numB = parseInt(b.fullname.match(/^\d+/)?.[0] || 999);
        return numA - numB;
      });
      
      setCourses(sorted);
      // Store courses in context so they're available throughout the app
      storeCourses(sorted);
      console.log('[DASHBOARD] Stored course IDs:', sorted.map(c => `${c.id} (${c.fullname})`));
    } catch (err) {
      console.error('Error loading courses:', err);
      setError(err.response?.data?.message || 'Failed to load courses');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await onLogout();
  };

  const getTestType = (shortname) => {
    const match = shortname.match(/Knowledge Test|Performance Test/);
    return match ? match[0] : shortname;
  };

  return (
    <div className="dashboard">
      <header className="header">
        <h1>Remediation Reports</h1>
        <div className="user-info">
          <span>Welcome, {user?.username}</span>
          <a href={`${import.meta.env.BASE_URL}admin`} className="admin-link" style={{marginLeft: 16, fontWeight: 'bold', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer'}}>Admin</a>
          <button onClick={handleLogout} className="logout-button">Log Out</button>
        </div>
      </header>

      <main className="main-content">
        {loading && <div className="loading">Loading courses...</div>}
        
        {error && <div className="error-message">{error}</div>}
        
        {!loading && courses.length === 0 && (
          <div className="no-courses">
            <p>No test courses available</p>
          </div>
        )}
        
        {!loading && courses.length > 0 && (
          <div className="courses-grid">
            {courses.map((course) => (
              <div key={course.id} className="course-card">
                <h2>{course.fullname}</h2>
                <p className="course-shortname">{getTestType(course.shortname)}</p>
                
                <div className="course-actions">
                  <button 
                    className="btn-primary"
                    onClick={() => handleCourseDetails(course.id, course.fullname)}
                  >
                    Course Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
